import './HomePage.css';

import {useEffect, useMemo, useState} from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {useAuth} from '../../contexts/AuthContext';
import {useEmployeeRecordings} from '../../hooks/useEmployeeRecordings';
import {parseISOWithoutConversion} from '../../utils/dates';
import {ChartOrMessage} from '../common';
import EmployeeImpairmentHeatmap from './Heatmap/EmployeeImpairmentHeatmap';

const HomePage = () => {
  const {user} = useAuth();
  const {
    data: recordingsData = [],
    isLoading: loading,
    error: recordingsError,
  } = useEmployeeRecordings();
  const error = recordingsError ? 'Failed to fetch data' : null;
  const [totalImpaired, setTotalImpaired] = useState(0);
  const [avgImpairedPerWeek, setAvgImpairedPerWeek] = useState(0);
  const [chartData, setChartData] = useState({
    complianceVsImpairment: [],
    avgWeeklyCheckins: [],
    impairmentTrend: [],
    impairmentByDay: [],
    timeOfDayData: [],
  });

  const currentDate = new Date();
  const [filterOpen, setFilterOpen] = useState(false);
  const [dateRange, setDateRange] = useState({
    start: '',
    end: '',
    isAllTime: true,
  });
  const [tempDateRange, setTempDateRange] = useState({
    start: '',
    end: '',
    isAllTime: true,
  });
  const [dateError, setDateError] = useState(null);

  useEffect(() => {
    if (recordingsData.length === 0) return;

    let startDate, endDate;

    if (dateRange.isAllTime) {
      if (recordingsData.length > 0) {
        const dates = recordingsData.map((record) => {
          const parsed = parseISOWithoutConversion(
            record.created_dt_in_org_zone,
          );
          return parsed ? parsed.toDate().getTime() : 0;
        });
        startDate = new Date(Math.min(...dates));
        startDate.setDate(1);
        startDate.setHours(0, 0, 0, 0);
      } else {
        startDate = new Date();
        startDate.setMonth(startDate.getMonth() - 11);
        startDate.setDate(1);
        startDate.setHours(0, 0, 0, 0);
      }

      endDate = new Date();
      endDate.setDate(
        new Date(endDate.getFullYear(), endDate.getMonth() + 1, 0).getDate(),
      );
      endDate.setHours(23, 59, 59, 999);
    } else {
      if (dateRange.start) {
        const [startYear, startMonth, startDay] = dateRange.start
          .split('-')
          .map((num) => parseInt(num, 10));
        startDate = new Date(startYear, startMonth - 1, startDay, 0, 0, 0, 0);
      } else {
        startDate = new Date(2024, 4, 1, 0, 0, 0, 0);
      }

      if (dateRange.end) {
        const [endYear, endMonth, endDay] = dateRange.end
          .split('-')
          .map((num) => parseInt(num, 10));
        endDate = new Date(endYear, endMonth - 1, endDay, 23, 59, 59, 999);
      } else {
        const now = new Date();
        const lastDay = new Date(
          now.getFullYear(),
          now.getMonth() + 1,
          0,
        ).getDate();
        endDate = new Date(
          now.getFullYear(),
          now.getMonth(),
          lastDay,
          23,
          59,
          59,
          999,
        );
      }
    }

    const filteredRecordings = recordingsData.filter((recording) => {
      const parsed = parseISOWithoutConversion(
        recording.created_dt_in_org_zone,
      );
      if (!parsed) return false;
      const recordingDate = parsed.toDate();
      return recordingDate >= startDate && recordingDate <= endDate;
    });

    const impairedCount = filteredRecordings.filter(
      (rec) => rec.result === '1' || rec.result === 1,
    ).length;
    setTotalImpaired(impairedCount);

    if (filteredRecordings.length > 0) {
      const totalWeeks = Math.max(
        1,
        Math.ceil((endDate - startDate) / (7 * 24 * 60 * 60 * 1000)),
      );
      setAvgImpairedPerWeek(Math.round((impairedCount / totalWeeks) * 10) / 10);
    } else {
      setAvgImpairedPerWeek(0);
    }

    const complianceVsImpairment = processComplianceVsImpairmentData(
      filteredRecordings,
      startDate,
      endDate,
    );
    const avgWeeklyCheckins = processAvgWeeklyCheckinsData(
      filteredRecordings,
      startDate,
      endDate,
    );
    const impairmentTrend = processImpairmentTrendData(
      filteredRecordings,
      startDate,
      endDate,
    );
    const impairmentByDay = processImpairmentByDayData(filteredRecordings);
    const timeOfDayData = processTimeOfDayData(filteredRecordings);

    setChartData({
      complianceVsImpairment,
      avgWeeklyCheckins,
      impairmentTrend,
      impairmentByDay,
      timeOfDayData,
    });
  }, [recordingsData, dateRange]);

  const processComplianceVsImpairmentData = (
    recordings,
    startDate,
    endDate,
  ) => {
    const monthlyData = getMonthsInRange(startDate, endDate);

    recordings.forEach((recording) => {
      const parsed = parseISOWithoutConversion(
        recording.created_dt_in_org_zone,
      );
      if (!parsed) return;
      const recordMonth = parsed.month - 1; // Convert to 0-indexed
      const recordYear = parsed.year;

      const matchingMonth = monthlyData.find(
        (m) => m.monthIndex === recordMonth && m.year === recordYear,
      );

      if (matchingMonth) {
        matchingMonth.checkins += 1;
        if (recording.result === '1' || recording.result === 1) {
          matchingMonth.impaired += 1;
        }
      }
    });

    return monthlyData;
  };

  const processAvgWeeklyCheckinsData = (recordings, startDate, endDate) => {
    const monthlyData = getMonthsInRange(startDate, endDate);

    recordings.forEach((recording) => {
      const parsed = parseISOWithoutConversion(
        recording.created_dt_in_org_zone,
      );
      if (!parsed) return;
      const recordMonth = parsed.month - 1; // Convert to 0-indexed
      const recordYear = parsed.year;

      const matchingMonth = monthlyData.find(
        (m) => m.monthIndex === recordMonth && m.year === recordYear,
      );

      if (matchingMonth) {
        matchingMonth.totalCheckins += 1;
      }
    });

    return monthlyData.map((month) => {
      const daysInMonth = new Date(
        month.year,
        month.monthIndex + 1,
        0,
      ).getDate();
      const weeksInMonth = daysInMonth / 7;
      const avgWeekly =
        weeksInMonth > 0
          ? Math.round((month.totalCheckins / weeksInMonth) * 10) / 10
          : 0;

      return {
        ...month,
        avgWeekly,
      };
    });
  };

  const processImpairmentTrendData = (recordings, startDate, endDate) => {
    const monthlyData = getMonthsInRange(startDate, endDate);

    recordings.forEach((recording) => {
      if (recording.result === '1' || recording.result === 1) {
        const parsed = parseISOWithoutConversion(
          recording.created_dt_in_org_zone,
        );
        if (!parsed) return;
        const recordMonth = parsed.month - 1; // Convert to 0-indexed
        const recordYear = parsed.year;

        const matchingMonth = monthlyData.find(
          (m) => m.monthIndex === recordMonth && m.year === recordYear,
        );

        if (matchingMonth) {
          matchingMonth.impaired += 1;
        }
      }
    });

    return monthlyData;
  };

  const processImpairmentByDayData = (recordings) => {
    const dayNames = ['Sun', 'Mon', 'Tues', 'Wed', 'Thurs', 'Fri', 'Sat'];
    const fullDayNames = [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
    ];
    const dayData = dayNames.map((day, index) => ({
      day,
      fullDay: fullDayNames[index],
      impaired: 0,
    }));

    recordings.forEach((recording) => {
      if (recording.result === '1' || recording.result === 1) {
        const parsed = parseISOWithoutConversion(
          recording.created_dt_in_org_zone,
        );
        if (!parsed) return;
        const dayOfWeek = parsed.toDate().getDay();
        dayData[dayOfWeek].impaired += 1;
      }
    });

    return dayData;
  };

  const processTimeOfDayData = (recordings) => {
    const scatterData = [];

    recordings.forEach((recording, index) => {
      const parsed = parseISOWithoutConversion(
        recording.created_dt_in_org_zone,
      );
      if (!parsed) return;
      const hour = parsed.hours;
      const minute = parsed.minutes;
      const dayOfWeek = parsed.toDate().getDay();
      const timeDecimal = hour + minute / 60; // Convert to decimal hours for precise positioning

      scatterData.push({
        x: dayOfWeek,
        y: timeDecimal,
        hour: hour,
        minute: minute,
        formattedTime: `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`,
        dayName: [
          'Sunday',
          'Monday',
          'Tuesday',
          'Wednesday',
          'Thursday',
          'Friday',
          'Saturday',
        ][dayOfWeek],
        isImpaired: recording.result === '1' || recording.result === 1,
        id: index,
      });
    });

    return scatterData;
  };

  const getMonthsInRange = (startDate, endDate) => {
    const months = [];
    const currentDate = new Date(startDate);
    const monthNames = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    const fullMonthNames = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];

    currentDate.setDate(1);
    currentDate.setHours(0, 0, 0, 0);

    while (currentDate <= endDate) {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();

      const formattedMonth = `${monthNames[month]} ${(year % 100).toString().padStart(2, '0')}`;
      const fullMonth = `${fullMonthNames[month]} ${year}`;

      months.push({
        month: formattedMonth,
        fullMonth: fullMonth,
        monthIndex: month,
        year: year,
        checkins: 0,
        impaired: 0,
        totalCheckins: 0,
        date: `${year}-${(month + 1).toString().padStart(2, '0')}-15`,
      });

      currentDate.setMonth(currentDate.getMonth() + 1);
      currentDate.setDate(1);
    }

    return months;
  };

  const formatDate = (date) => {
    const options = {month: 'short', day: 'numeric', year: 'numeric'};
    return date.toLocaleDateString('en-US', options);
  };

  const getDateRangeText = () => {
    if (dateRange.isAllTime) {
      if (recordingsData.length > 0) {
        const dates = recordingsData.map((record) => {
          const parsed = parseISOWithoutConversion(
            record.created_dt_in_org_zone,
          );
          return parsed ? parsed.toDate().getTime() : 0;
        });
        const firstDate = new Date(Math.min(...dates));
        const endDate = new Date();

        const monthNames = [
          'Jan',
          'Feb',
          'Mar',
          'Apr',
          'May',
          'Jun',
          'Jul',
          'Aug',
          'Sep',
          'Oct',
          'Nov',
          'Dec',
        ];
        const formattedStart = `${monthNames[firstDate.getMonth()]} ${firstDate.getFullYear()}`;
        const formattedEnd = `${monthNames[endDate.getMonth()]} ${endDate.getFullYear()}`;

        return `All time, ${formattedStart} - ${formattedEnd}`;
      } else {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setMonth(startDate.getMonth() - 11);

        const monthNames = [
          'Jan',
          'Feb',
          'Mar',
          'Apr',
          'May',
          'Jun',
          'Jul',
          'Aug',
          'Sep',
          'Oct',
          'Nov',
          'Dec',
        ];
        const formattedStart = `${monthNames[startDate.getMonth()]} ${startDate.getFullYear()}`;
        const formattedEnd = `${monthNames[endDate.getMonth()]} ${endDate.getFullYear()}`;

        return `All time, ${formattedStart} - ${formattedEnd}`;
      }
    } else {
      let startText = '';
      let endText = '';

      if (dateRange.start) {
        const [startYear, startMonth, startDay] = dateRange.start
          .split('-')
          .map((num) => parseInt(num, 10));
        const monthNames = [
          'Jan',
          'Feb',
          'Mar',
          'Apr',
          'May',
          'Jun',
          'Jul',
          'Aug',
          'Sep',
          'Oct',
          'Nov',
          'Dec',
        ];
        startText = `${monthNames[startMonth - 1]} ${startDay} ${startYear}`;
      }

      if (dateRange.end) {
        const [endYear, endMonth, endDay] = dateRange.end
          .split('-')
          .map((num) => parseInt(num, 10));
        const monthNames = [
          'Jan',
          'Feb',
          'Mar',
          'Apr',
          'May',
          'Jun',
          'Jul',
          'Aug',
          'Sep',
          'Oct',
          'Nov',
          'Dec',
        ];
        endText = `${monthNames[endMonth - 1]} ${endDay} ${endYear}`;
      }

      return `${startText} - ${endText}`;
    }
  };

  const handleFilterClick = () => {
    if (!filterOpen) {
      setTempDateRange({...dateRange});
      setDateError(null);
    }
    setFilterOpen(!filterOpen);
  };

  const handleDateChange = (e) => {
    const {name, value} = e.target;

    setTempDateRange((prev) => ({
      ...prev,
      [name]: value,
      isAllTime: false,
    }));

    setDateError(null);
  };

  const handleFilterApply = () => {
    const newDateRange = {...tempDateRange};

    let startDate, endDate;
    const today = new Date();
    today.setHours(23, 59, 59, 999);

    if (newDateRange.start) {
      const [startYear, startMonth, startDay] = newDateRange.start
        .split('-')
        .map((num) => parseInt(num, 10));
      startDate = new Date(startYear, startMonth - 1, startDay);
    }

    if (newDateRange.end) {
      const [endYear, endMonth, endDay] = newDateRange.end
        .split('-')
        .map((num) => parseInt(num, 10));
      endDate = new Date(endYear, endMonth - 1, endDay);
    }

    // Only check if start date is in the future - remove end date future check
    if (
      newDateRange.start &&
      startDate &&
      !isNaN(startDate.getTime()) &&
      startDate > today
    ) {
      setDateError('Start date cannot be in the future');
      return;
    }

    // Keep the start > end validation
    if (
      newDateRange.start &&
      newDateRange.end &&
      startDate &&
      !isNaN(startDate.getTime()) &&
      endDate &&
      !isNaN(endDate.getTime())
    ) {
      if (startDate > endDate) {
        setDateError('Start date cannot be after end date');
        return;
      }
    }

    if (!newDateRange.start && !newDateRange.isAllTime) {
      const orgStartDate = new Date(2024, 4, 1);
      const year = orgStartDate.getFullYear();
      const month = (orgStartDate.getMonth() + 1).toString().padStart(2, '0');
      const day = orgStartDate.getDate().toString().padStart(2, '0');
      newDateRange.start = `${year}-${month}-${day}`;
    }

    if (!newDateRange.end && !newDateRange.isAllTime) {
      const todayLocal = new Date();
      const year = todayLocal.getFullYear();
      const month = (todayLocal.getMonth() + 1).toString().padStart(2, '0');
      const day = todayLocal.getDate().toString().padStart(2, '0');
      newDateRange.end = `${year}-${month}-${day}`;
    }

    setDateRange(newDateRange);
    setFilterOpen(false);
  };

  const handleReset = () => {
    const resetFilter = {
      start: '',
      end: '',
      isAllTime: true,
    };

    setTempDateRange(resetFilter);
    setDateRange(resetFilter);
    setDateError(null);
    setFilterOpen(false);
  };

  const handleCancel = () => {
    setFilterOpen(false);
    setDateError(null);
  };

  // Heatmap range: default to the full current calendar month when no filter is
  // applied; otherwise respect the user's date filter.
  const heatmapRange = useMemo(() => {
    if (!dateRange.isAllTime && dateRange.start && dateRange.end) {
      const [sy, sm, sd] = dateRange.start.split('-').map(Number);
      const [ey, em, ed] = dateRange.end.split('-').map(Number);
      return {
        start: new Date(sy, sm - 1, sd),
        end: new Date(ey, em - 1, ed),
      };
    }
    const now = new Date();
    return {
      start: new Date(now.getFullYear(), now.getMonth(), 1),
      end: new Date(now.getFullYear(), now.getMonth() + 1, 0),
    };
  }, [dateRange]);

  const CustomBar = (props) => {
    const {fill, x, y, width, height, payload} = props;

    if (payload && payload[props.dataKey] === 0) {
      return (
        <rect
          x={x}
          y={y + height - 2}
          width={width}
          height={2}
          fill={fill}
          opacity={0.5}
        />
      );
    }
    return <rect x={x} y={y} width={width} height={height} fill={fill} />;
  };

  const CustomScatterDot = (props) => {
    const {cx, cy, payload} = props;
    const color = payload.isImpaired ? '#ff0000' : '#00c9a7';
    return <circle cx={cx} cy={cy} r={4} fill={color} />;
  };

  // Custom Y-axis tick formatter for time
  const formatYAxisTime = (tickItem) => {
    const hour = Math.floor(tickItem);
    const minute = Math.round((tickItem - hour) * 60);
    return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
  };

  if (loading) return <div className='loading'>Loading data...</div>;
  if (error) return <div className='error'>{error}</div>;

  return (
    <div className='home-content'>
      <div className='date-section'>
        <div className='date-display'>{formatDate(currentDate)}</div>
      </div>

      <div className='dashboard-header-container'>
        <div className='date-range-display'>
          <div className='filter-container'>
            <button className='filter-button' onClick={handleFilterClick}>
              Filter
            </button>
            {filterOpen && (
              <div className='filter-popup'>
                <h3>Filter by Date Range</h3>
                {dateError && <div className='date-error'>{dateError}</div>}
                <div className='filter-inputs'>
                  <div className='filter-input-group'>
                    <label htmlFor='start'>Start Date</label>
                    <input
                      type='date'
                      id='start'
                      name='start'
                      value={tempDateRange.start}
                      onChange={handleDateChange}
                    />
                  </div>
                  <div className='filter-input-group'>
                    <label htmlFor='end'>End Date</label>
                    <input
                      type='date'
                      id='end'
                      name='end'
                      value={tempDateRange.end}
                      onChange={handleDateChange}
                    />
                  </div>
                </div>
                <div className='filter-actions'>
                  <button
                    className='filter-apply-button'
                    onClick={handleFilterApply}
                  >
                    Apply Filter
                  </button>
                  <button className='filter-reset-button' onClick={handleReset}>
                    Reset
                  </button>
                  <button
                    className='filter-cancel-button'
                    onClick={handleCancel}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
          <span className='date-range-text'>{getDateRangeText()}</span>
        </div>
      </div>

      <div className='stats-section'>
        <div className='stat-card total-impaired'>
          <div className='stat-number'>{totalImpaired}</div>
          <div className='stat-label'>Times Impairment Identified</div>
        </div>

        <div className='stat-card avg-impaired'>
          <div className='stat-number'>{avgImpairedPerWeek}</div>
          <div className='stat-label'>
            AVG Times Impairment Identified Per Week
          </div>
        </div>
      </div>

      <div className='charts-section-row'>
        {/* <EmployeeImpairmentHeatmap
          endDate={heatmapRange.end}
          recordings={recordingsData}
          startDate={heatmapRange.start}
        /> */}

        <div className='chart-container'>
          <h3>CHECK-IN VS IMPAIRMENT IDENTIFIED</h3>
          <div className='chart'>
            <ChartOrMessage data={chartData.complianceVsImpairment}>
              <ResponsiveContainer width='100%' height={200}>
                <BarChart data={chartData.complianceVsImpairment}>
                  <CartesianGrid vertical={false} strokeDasharray='3 3' />
                  <XAxis dataKey='month' />
                  <YAxis allowDecimals={false} domain={[0, 'dataMax']} />
                  <Tooltip
                    labelFormatter={(label, payload) => {
                      if (payload && payload.length > 0) {
                        return payload[0].payload.fullMonth;
                      }
                      return label;
                    }}
                  />
                  <Bar
                    dataKey='checkins'
                    fill='#00c9a7'
                    name='Check-ins'
                    shape={<CustomBar />}
                  />
                  <Bar
                    dataKey='impaired'
                    fill='#ff0000'
                    name='Impairment Identified'
                    shape={<CustomBar />}
                  />
                </BarChart>
              </ResponsiveContainer>
            </ChartOrMessage>
          </div>
        </div>

      </div>

      <div className='charts-section-row'>
        <div className='chart-container'>
          <h3>AVG WEEKLY CHECK-IN (MONTHLY)</h3>
          <div className='chart'>
            <ChartOrMessage data={chartData.avgWeeklyCheckins}>
              <ResponsiveContainer width='100%' height={200}>
                <BarChart data={chartData.avgWeeklyCheckins}>
                  <CartesianGrid vertical={false} strokeDasharray='3 3' />
                  <XAxis dataKey='month' />
                  <YAxis allowDecimals={false} domain={[0, 'dataMax']} />
                  <Tooltip
                    labelFormatter={(label, payload) => {
                      if (payload && payload.length > 0) {
                        return payload[0].payload.fullMonth;
                      }
                      return label;
                    }}
                  />
                  <Bar
                    dataKey='avgWeekly'
                    fill='#2196f3'
                    name='Avg Weekly Check-ins'
                  />
                </BarChart>
              </ResponsiveContainer>
            </ChartOrMessage>
          </div>
        </div>

        <div className='chart-container'>
          <h3>IMPAIRMENT IDENTIFIED TREND</h3>
          <div className='chart'>
            <ChartOrMessage
              message='You have never been identified as impaired'
              data={chartData.impairmentTrend}
            >
              <ResponsiveContainer width='100%' height={200}>
                <LineChart data={chartData.impairmentTrend}>
                  <CartesianGrid vertical={false} strokeDasharray='3 3' />
                  <XAxis dataKey='month' />
                  <YAxis allowDecimals={false} domain={[0, 'dataMax']} />
                  <Tooltip
                    labelFormatter={(label, payload) => {
                      if (payload && payload.length > 0) {
                        return payload[0].payload.fullMonth;
                      }
                      return label;
                    }}
                  />
                  <Line
                    type='monotone'
                    dataKey='impaired'
                    stroke='#ff0000'
                    activeDot={{r: 8}}
                    name='Impairment Identified'
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartOrMessage>
          </div>
        </div>

      </div>

      <div className='charts-section-row'>
        <div className='chart-container'>
          <h3>IMPAIRMENT BY DAY OF WEEK</h3>
          <div className='chart'>
            <ChartOrMessage
              message='You have never been identified as impaired'
              data={chartData.impairmentByDay}
            >
              <ResponsiveContainer width='100%' height={200}>
                <BarChart data={chartData.impairmentByDay}>
                  <CartesianGrid vertical={false} strokeDasharray='3 3' />
                  <XAxis dataKey='day' />
                  <YAxis allowDecimals={false} domain={[0, 'dataMax']} />
                  <Tooltip
                    formatter={(value, name) => [value, name]}
                    labelFormatter={(label, payload) => {
                      if (payload && payload.length > 0) {
                        return payload[0].payload.fullDay;
                      }
                      return label;
                    }}
                  />
                  <Bar
                    dataKey='impaired'
                    fill='#9c27b0'
                    name='Impairment Identified'
                  />
                </BarChart>
              </ResponsiveContainer>
            </ChartOrMessage>
          </div>
        </div>

        <div className='chart-container'>
          <h3>CHECK-IN AND IMPAIRMENT BY TIME OF DAY</h3>
          <div className='chart-large'>
            <ChartOrMessage data={chartData.timeOfDayData}>
              <ResponsiveContainer width='100%' height={300}>
                <ScatterChart data={chartData.timeOfDayData}>
                  <CartesianGrid vertical={false} strokeDasharray='3 3' />
                  <XAxis
                    dataKey='x'
                    type='number'
                    domain={[0, 6]}
                    ticks={[0, 1, 2, 3, 4, 5, 6]}
                    tickFormatter={(value) =>
                      ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][value]
                    }
                  />
                  <YAxis
                    allowDecimals={false}
                    dataKey='y'
                    type='number'
                    domain={[0, 'dataMax']}
                    tickFormatter={formatYAxisTime}
                  />
                  <Tooltip
                    formatter={(value, name, props) => {
                      if (name === 'y') {
                        return [props.payload.formattedTime, 'Time'];
                      }
                      if (name === 'x') {
                        return [props.payload.dayName, 'Day'];
                      }
                      return [value, name];
                    }}
                  />
                  <Scatter dataKey='y' shape={<CustomScatterDot />} />
                </ScatterChart>
              </ResponsiveContainer>
            </ChartOrMessage>
          </div>
          <div className='legend'>
            <div className='legend-item'>
              <div
                className='legend-color'
                style={{backgroundColor: '#00c9a7'}}
              ></div>
              <span>Check-in</span>
            </div>
            <div className='legend-item'>
              <div
                className='legend-color'
                style={{backgroundColor: '#ff0000'}}
              ></div>
              <span>Impairment Identified</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
